import React, { useState, useRef, useCallback, useEffect } from "react";
import { Input, InputProps } from "../ui/input";
import { Button } from "../ui/button";
import {
  File as FileIcon,
  Backspace,
  FileCode,
  FileCsv,
  FileDoc,
  FileImage,
  FilePdf,
  FilePlus,
  FilePpt,
  FileText,
  FileXls,
  FileZip,
  SpinnerGap,
  UploadSimple,
  Warning,
} from "@phosphor-icons/react";
import useFileUpload from "@/lib/queries/tools/useFileUpload";
import { sha256 } from "js-sha256";
import { useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const FILE_ICON_SIZE = 24;
const FILE_ICON_WEIGHT = "regular";

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

interface FileInputProps extends InputProps {
  onFilesChange?: (files: File[]) => void;
  maxFileSize?: number; // in bytes, optional file size limit
}

const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  ({ onFilesChange, maxFileSize, className, ...props }, ref) => {
    const { t } = useTranslation("common");
    const form = useFormContext();
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploadingFiles, setUploadingFiles] = useState<Set<number>>(
      new Set()
    );
    const [isDragging, setIsDragging] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const { mutateAsync, data: _data } = useFileUpload(false);

    const processFiles = useCallback(
      (files: File[]) => {
        // Clear any previous error and warning messages
        setErrorMessage(null);
        setWarningMessage(null);

        const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
        const WARNING_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

        // Filter out files that exceed the size limit and collect rejected files
        const validFiles: File[] = [];
        const rejectedFiles: File[] = [];
        const largeFiles: File[] = [];

        files.forEach((file) => {
          if (file.size > MAX_FILE_SIZE) {
            rejectedFiles.push(file);
          } else if (maxFileSize && file.size > maxFileSize) {
            // Additional custom file size limit if provided
            rejectedFiles.push(file);
          } else {
            validFiles.push(file);
            if (file.size > WARNING_FILE_SIZE) {
              largeFiles.push(file);
            }
          }
        });

        // Show error for rejected files
        if (rejectedFiles.length > 0) {
          const fileNames = rejectedFiles.map((f) => f.name).join(", ");
          const errorMessage =
            rejectedFiles.length === 1
              ? t("fileInput.error.fileSizeExceeded", {
                  fileName: rejectedFiles[0].name,
                  fileSize: formatFileSize(rejectedFiles[0].size),
                })
              : t("fileInput.error.multipleFileSizeExceeded", {
                  count: rejectedFiles.length,
                  fileNames,
                });

          setErrorMessage(errorMessage);
          console.error("Files rejected due to size limit:", rejectedFiles);
        }

        // Show warnings for large files or multiple files
        if (validFiles.length > 0) {
          const totalFiles = [...selectedFiles, ...validFiles].length;
          const hasLargeFiles = largeFiles.length > 0;

          if (hasLargeFiles) {
            if (largeFiles.length === 1) {
              setWarningMessage(
                t("fileInput.warning.largefile", {
                  fileName: largeFiles[0].name,
                  fileSize: formatFileSize(largeFiles[0].size),
                })
              );
            } else {
              setWarningMessage(
                t("fileInput.warning.combinedWarning", { count: totalFiles })
              );
            }
          }
        }

        if (validFiles.length === 0) return;

        // Clear error and warning messages if we have valid files to process and no errors
        if (validFiles.length > 0 && rejectedFiles.length === 0) {
          setErrorMessage(null);
        }

        // Append new files to existing files instead of replacing
        setSelectedFiles((prevFiles) => [...prevFiles, ...validFiles]);

        // Calculate starting index for new files
        const startingIndex = selectedFiles.length;

        // Mark all new files as uploading
        const newUploadingFiles = new Set<number>();
        validFiles.forEach((_, i) => {
          newUploadingFiles.add(startingIndex + i);
        });

        // Update uploading files state
        setUploadingFiles((prev) => {
          const updated = new Set(prev);
          newUploadingFiles.forEach((index) => updated.add(index));
          return updated;
        });

        // Process each file
        validFiles.forEach((file, i) => {
          const index = startingIndex + i;
          const reader = new FileReader();

          reader.onload = async () => {
            const arrayBuffer = reader.result as ArrayBuffer;
            const hash = sha256(arrayBuffer);
            try {
              await mutateAsync({
                filename: file.name,
                hash,
                binary: arrayBuffer,
              }).then((data) => {
                // Get existing fileUrls and append new one
                const existingUrls = form.getValues("fileUrls") || [];
                form.setValue("fileUrls", [...existingUrls, data.filename]);

                const existingDisplayNames =
                  form.getValues("fileDisplayNames") || [];
                form.setValue("fileDisplayNames", [
                  ...existingDisplayNames,
                  file.name,
                ]);
              });
            } finally {
              setUploadingFiles((prev) => {
                const updated = new Set(prev);
                updated.delete(index);
                return updated;
              });
            }
          };
          reader.readAsArrayBuffer(file);
        });

        if (onFilesChange) {
          onFilesChange([...selectedFiles, ...validFiles]);
        }
      },
      [
        form,
        mutateAsync,
        onFilesChange,
        selectedFiles,
        maxFileSize,
        setErrorMessage,
        setWarningMessage,
        t,
      ]
    );

    // Keep UI list in sync with persisted form state after submit/remount
    // If local list is empty but form has fileUrls, rehydrate with placeholders
    // Prefer original display names if available
    const watchedFileUrls: string[] = form.watch?.("fileUrls") ?? [];
    const watchedDisplayNames: string[] =
      form.watch?.("fileDisplayNames") ?? [];
    useEffect(() => {
      if (
        selectedFiles.length === 0 &&
        Array.isArray(watchedFileUrls) &&
        watchedFileUrls.length > 0
      ) {
        // Create placeholder File objects for display purposes (size will be 0)
        // Prefer previously saved original names when available and aligned
        const sourceNames =
          Array.isArray(watchedDisplayNames) &&
          watchedDisplayNames.length === watchedFileUrls.length
            ? watchedDisplayNames
            : watchedFileUrls;
        const placeholders = sourceNames.map((name) => new File([], name));
        setSelectedFiles(placeholders);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedFileUrls, watchedDisplayNames]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      processFiles(files);
    };

    const handleRemoveFile = async (index: number, e: React.MouseEvent) => {
      e.stopPropagation();

      const newFiles = selectedFiles.filter((_, i) => i !== index);
      setSelectedFiles(newFiles);

      // Also remove the corresponding file URL from the form
      const existingUrls = form.getValues("fileUrls") || [];
      if (existingUrls.length > index) {
        const updatedUrls = existingUrls.filter(
          (_: string, i: number) => i !== index
        );
        form.setValue("fileUrls", updatedUrls);
      }

      // Keep display names array in sync as well
      const existingDisplayNames = form.getValues("fileDisplayNames") || [];
      if (existingDisplayNames.length > index) {
        const updatedDisplayNames = existingDisplayNames.filter(
          (_: string, i: number) => i !== index
        );
        form.setValue("fileDisplayNames", updatedDisplayNames);
      }

      setUploadingFiles((prev) => {
        const updated = new Set(prev);
        updated.delete(index);
        return updated;
      });

      // Update warnings based on remaining files
      const WARNING_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
      const largeFiles = newFiles.filter(
        (file) => file.size > WARNING_FILE_SIZE
      );
      const hasLargeFiles = largeFiles.length > 0;

      if (hasLargeFiles) {
        if (largeFiles.length === 1) {
          setWarningMessage(
            t("fileInput.warning.largefile", {
              fileName: largeFiles[0].name,
              fileSize: formatFileSize(largeFiles[0].size),
            })
          );
        } else {
          setWarningMessage(
            t("fileInput.warning.combinedWarning", { count: newFiles.length })
          );
        }
      } else {
        setWarningMessage(null);
      }

      if (onFilesChange) {
        onFilesChange(newFiles);
      }

      if (inputRef.current) {
        inputRef.current.value = "";
      }
    };

    const openFileDialog = (_e: React.MouseEvent) => {
      inputRef.current?.click();
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setIsDragging(false);
      }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);
        processFiles(files);
      }
    };

    const getFileIcon = (name: string): React.ReactNode => {
      const extension = name.split(".").pop()?.toLowerCase() || "";

      switch (extension) {
        case "pdf":
          return <FilePdf size={FILE_ICON_SIZE} weight={FILE_ICON_WEIGHT} />;
        case "png":
        case "jpg":
        case "jpeg":
        case "gif":
        case "bmp":
        case "svg":
          return <FileImage size={FILE_ICON_SIZE} weight={FILE_ICON_WEIGHT} />;
        case "doc":
        case "docx":
          return <FileDoc size={FILE_ICON_SIZE} weight={FILE_ICON_WEIGHT} />;
        case "xls":
        case "xlsx":
          return <FileXls size={FILE_ICON_SIZE} weight={FILE_ICON_WEIGHT} />;
        case "ppt":
        case "pptx":
          return <FilePpt size={FILE_ICON_SIZE} weight={FILE_ICON_WEIGHT} />;
        case "zip":
        case "rar":
        case "7z":
          return <FileZip size={FILE_ICON_SIZE} weight={FILE_ICON_WEIGHT} />;
        case "js":
        case "jsx":
        case "ts":
        case "tsx":
        case "html":
        case "css":
          return <FileCode size={FILE_ICON_SIZE} weight={FILE_ICON_WEIGHT} />;
        case "csv":
          return <FileCsv size={FILE_ICON_SIZE} weight={FILE_ICON_WEIGHT} />;
        case "txt":
          return <FileText size={FILE_ICON_SIZE} weight={FILE_ICON_WEIGHT} />;
        default:
          return <FileIcon size={FILE_ICON_SIZE} weight={FILE_ICON_WEIGHT} />;
      }
    };

    return (
      <div
        className={cn(
          "relative flex flex-col p-4 border-2 border-transparent space-y-2 bg-white border-t border-t-light-grey rounded-b-md transition-all duration-200",
          isDragging && "bg-secondary border-dashed border-secondary",
          className
        )}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Input
          type="file"
          accept=".doc,.docm,.docx,.dot,.dotx,.dps,.et,.hwp,.lwp,.md,.odp,.ods,.odt,.pdf,.pps,.ppsx,.ppt,.pptm,.pptx,.rst,.rtf,.txt,.wpd,.wps,.xls,.xlsm,.xlsx,.xps,.zabw"
          multiple
          onChange={handleFileChange}
          ref={(element) => {
            if (typeof ref === "function") {
              ref(element);
            } else if (ref) {
              ref.current = element;
            }
            inputRef.current = element;
          }}
          {...props}
          className="text-transparent bg-transparent !border-0 rounded-none hidden"
        />
        <Button
          type="button"
          variant={isDragging ? "default" : "ghost"}
          size="sm"
          title={
            !isDragging
              ? t("fileInput.uploadButton")
              : t("fileInput.dropMessage")
          }
          className={cn(
            "justify-between w-full px-2 py-2 text-light-grey rounded-none hover:text-grey border border-transparent truncate",
            isDragging && "bg-secondary text-primary hover:bg-primary"
          )}
          onClick={openFileDialog}
        >
          {isDragging ? (
            <>{t("fileInput.dropMessage")}</>
          ) : (
            <>
              <span title={t("fileInput.uploadButton")} className="truncate">
                {t("fileInput.uploadButton")}
              </span>
              {selectedFiles.length > 0 && (
                <span className="!w-7 !h-7 p-1 ml-auto text-center font-bold border rounded-full aspect-square">
                  {selectedFiles.length}
                </span>
              )}
            </>
          )}
          {isDragging ? (
            <UploadSimple
              size={23}
              className="ml-2 !w-5 !h-5 text-primary"
              weight="bold"
            />
          ) : (
            <FilePlus
              size={23}
              className="ml-2 !w-5 !h-5 text-inherit"
              weight="bold"
            />
          )}
        </Button>
        {errorMessage && (
          <div className="p-3 text-sm text-red-700 border border-red-200 rounded-md bg-red-50">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="w-5 h-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p>{errorMessage}</p>
              </div>
              <div className="pl-3 ml-auto">
                <button
                  type="button"
                  className="inline-flex text-red-400 hover:text-red-600"
                  onClick={() => setErrorMessage(null)}
                >
                  <span className="sr-only">{t("fileInput.dismiss")}</span>
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
        {warningMessage && (
          <div className="p-3 text-sm border rounded-md text-amber-700 bg-amber-50 border-amber-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <Warning className="w-5 h-5 text-amber-400" />
              </div>
              <div className="ml-3">
                <p>{warningMessage}</p>
              </div>
              <div className="pl-3 ml-auto">
                <button
                  type="button"
                  className="inline-flex text-amber-400 hover:text-amber-600"
                  onClick={() => setWarningMessage(null)}
                >
                  <span className="sr-only">{t("fileInput.dismiss")}</span>
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
        {selectedFiles.length > 0 && (
          <ul className="space-y-2">
            {selectedFiles.map((file, index) => {
              let randomRotation =
                "rotate-[" + Math.floor(Math.random() * 360) + "deg]";
              return (
                <li
                  key={index}
                  className="flex items-center justify-between max-w-full px-2"
                >
                  <div className="flex items-center flex-grow w-full gap-2">
                    {uploadingFiles.has(index) ? (
                      <SpinnerGap
                        size={FILE_ICON_SIZE}
                        weight={FILE_ICON_WEIGHT}
                        className={cn(
                          "animate-spin text-primary",
                          randomRotation
                        )}
                      />
                    ) : (
                      getFileIcon(file.name)
                    )}
                    <div className="flex max-w-full min-w-0">
                      <div className="flex text-sm">
                        {(() => {
                          const lastDotIndex = file.name.lastIndexOf(".");
                          const baseName =
                            lastDotIndex > 0
                              ? file.name.substring(0, lastDotIndex)
                              : file.name;
                          const extension =
                            lastDotIndex > 0
                              ? file.name.substring(lastDotIndex)
                              : "";

                          return (
                            <>
                              <span className="truncate max-w-52">
                                {baseName}
                              </span>
                              <span className="flex-shrink-0 text-light-grey">
                                {extension}
                              </span>
                            </>
                          );
                        })()}
                        {uploadingFiles.has(index) ? (
                          <span className="flex-shrink-0 ml-2 text-xs text-gray-500">
                            {t("fileInput.uploading")}
                          </span>
                        ) : file.size > 0 ? (
                          <span className="flex-shrink-0 ml-2 text-xs text-light-grey">
                            {formatFileSize(file.size)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    className="!w-7 !h-7 !border-0 hover:text-gray-500"
                    size="icon"
                    disabled={uploadingFiles.has(index)}
                    onClick={(e) => handleRemoveFile(index, e)}
                  >
                    <Backspace
                      size={20}
                      weight="bold"
                      className="!w-5 !h-5"
                      color={
                        uploadingFiles.has(index)
                          ? "hsl(var(--muted-foreground))"
                          : "hsl(var(--light-grey))"
                      }
                    />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }
);

FileInput.displayName = "FileInput";

export { FileInput };
